import { Info, MessageSquare, Phone } from 'lucide-react';
import { Card } from '@/components/ui/card';
import type { CampaignLaunchState } from '@/hooks/useCampaignLaunchState';
import { StepFooter, StepHeader } from './StepLayout';
import { WhatsAppTemplatePicker } from './TemplateFields';

interface ChannelStepProps {
  state: CampaignLaunchState;
  onBack: () => void;
  onNext: () => void;
}

/**
 * Step 2: the WhatsApp template that carries the message.
 *
 * The channel itself is NOT chosen here. It was decided by the Campaigns page tab the campaign was
 * created from, and is shown read-only so nobody wonders what the template is for. This step used to
 * offer SMS and WhatsApp as cards, which asked the same question twice and offered SMS from a tab
 * where SMS campaigns are "coming soon".
 *
 * The template is chosen here rather than with the launch because it shapes the steps after it: its
 * media header decides what the media step asks for, and its values are part of choosing it.
 */
export default function ChannelStep({ state, onBack, onNext }: ChannelStepProps) {
  const { channel, channelValid, templateBlockers } = state;
  const isWhatsApp = channel === 'whatsapp';
  const Icon = isWhatsApp ? MessageSquare : Phone;

  return (
    <Card className="p-6">
      <StepHeader step="channel" />

      <div className="flex items-center gap-3 rounded-lg bg-muted/50 px-4 py-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-background">
          <Icon className={isWhatsApp ? 'h-4 w-4 text-green-500' : 'h-4 w-4 text-blue-500'} />
        </div>
        <div className="text-sm">
          <p className="font-medium">Sending via {isWhatsApp ? 'WhatsApp' : 'SMS'}</p>
          <p className="text-muted-foreground">
            {isWhatsApp
              ? 'Set by the WhatsApp tab on the Campaigns page.'
              : 'This campaign was set up to send by text message.'}
          </p>
        </div>
      </div>

      <div className="mt-6 border-t pt-6">
        {isWhatsApp ? (
          <WhatsAppTemplatePicker state={state} />
        ) : (
          <div className="flex gap-3 rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <p>
              SMS has no templates. It sends the text of the media you choose on the next step, and
              you can write your own message there instead.
            </p>
          </div>
        )}
      </div>

      <StepFooter
        onBack={onBack}
        onNext={onNext}
        nextDisabled={!channelValid}
        hint={!channelValid ? templateBlockers[0] : undefined}
      />
    </Card>
  );
}
